import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, throwError } from 'rxjs';

import { NotificationService } from '../services/notification/notification.service';

/**
 * Surfaces BE policy-denial 409s (`{ code: <snake_case> }`) as toasts.
 * Belt-and-braces for optimistic writes that slipped past a
 * `policy().canX` gate — the FE policy service catches most of these
 * proactively; this stops the rest from failing silently.
 *
 * Unknown codes fall through to the BE `message` field or the
 * translation key so the user always sees something.
 */
const RECOGNISED_CODES = new Set([
  'assessment_locked',
  'review_in_progress',
  'measure_locked',
  'dpia_locked',
  'archived',
  'read_only_mode',
]);

export const policyDeniedInterceptor: HttpInterceptorFn = (req, next) => {
  const notifier = inject(NotificationService);
  const transloco = inject(TranslocoService);

  return next(req).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        const body = error.error as { code?: string; message?: string } | null;
        if (body?.code && RECOGNISED_CODES.has(body.code)) {
          const key = `policy_denied.${body.code}`;
          const translated = transloco.translate<string>(key);
          notifier.error(translated === key ? (body.message ?? key) : translated);
        }
      }
      return throwError(() => error);
    })
  );
};
